import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

// Base URL for the backend API for active CRM records and clients
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/excel/records`;
// URL for the new history record model
const HISTORY_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/excel/history`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable Modal Component (unchanged)
const Modal = ({ message, onConfirm, onCancel }) => {
    if (!message) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative p-5 bg-white w-96 rounded-lg shadow-xl">
                <div className="text-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{message}</h3>
                </div>
                <div className="mt-4 flex justify-center space-x-4">
                    {onConfirm && (
                        <button
                            onClick={onConfirm}
                            className="px-4 py-2 bg-green-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            Confirm
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                        {onConfirm ? 'Cancel' : 'OK'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExcelGen = () => {
    // State for managing active CRM records
    const [activeRecords, setActiveRecords] = useState([]);
    // State for managing records that have been moved to the history model
    const [historyRecords, setHistoryRecords] = useState([]);
    // State to track which CRM record row is currently being edited
    // NEW: Load initial state from sessionStorage
    const [editingRowId, setEditingRowId] = useState(
        () => sessionStorage.getItem('editingRowId') || null
    );
    // State to hold the data of the CRM record being edited
    // NEW: Load initial state from sessionStorage, parse it from JSON
    const [editedData, setEditedData] = useState(
        () => JSON.parse(sessionStorage.getItem('editedData')) || {}
    );
    // State to hold data for a new CRM record being added
    // NEW: Load initial state from sessionStorage, parse it from JSON
    const [newRecordData, setNewRecordData] = useState(
        () => JSON.parse(sessionStorage.getItem('newRecordData')) || {
            topic: '',
            audience: '',
            keywords: '',
            WP_SITE_URL: '',
            WP_USERNAME: '',
            WP_APP_PASSWORD: '',
            linkword: '',
            linkurl: '',
            featuredImage: '',
            shortblog: 'No',
            publishDate:'',
            publishTime:'',
        }
    );

    // --- STATES FOR CLIENTS FEATURE (unchanged) ---
    const [clients, setClients] = useState([]);
    const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
    const [error, setError] = useState(null);

    // --- STATES FOR GEMINI API INTERACTION (unchanged) ---
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    // --- STATES FOR MODAL AND DOWNLOAD STATUS (unchanged) ---
    const [modalMessage, setModalMessage] = useState('');
    const [modalAction, setModalAction] = useState(null);

    // --- REFS FOR CLICK-OUTSIDE LOGIC (unchanged) ---
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Initial fetch of records and clients
    useEffect(() => {
        fetchRecords();
        fetchClients();
    }, []);

    // NEW: Effect to save newRecordData to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('newRecordData', JSON.stringify(newRecordData));
    }, [newRecordData]);

    // NEW: Effect to save editingRowId to sessionStorage whenever it changes
    useEffect(() => {
        if (editingRowId) {
            sessionStorage.setItem('editingRowId', editingRowId);
        } else {
            sessionStorage.removeItem('editingRowId');
        }
    }, [editingRowId]);

    // NEW: Effect to save editedData to sessionStorage whenever it changes
    useEffect(() => {
        if (Object.keys(editedData).length > 0) {
            sessionStorage.setItem('editedData', JSON.stringify(editedData));
        } else {
            sessionStorage.removeItem('editedData');
        }
    }, [editedData]);


    // --- useEffect for handling clicks outside the dropdown (unchanged) ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isClientsDropdownOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsClientsDropdownOpen(false);
            }
        };

        if (isClientsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isClientsDropdownOpen]);

    // --- YOUR PROVIDED FUNCTION TO FETCH CLIENTS FROM BACKEND (unchanged) ---
    const fetchClients = async () => {
        const token = localStorage.getItem("token");
        try {
            if (!BACKEND_URL) {
                setError("Backend URL is not configured in environment variables. Please check your .env file.");
                console.error("REACT_APP_BACKEND_URL is not defined.");
                return;
            }
            const response = await axios.get(`${BACKEND_URL}/api/client`, {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true,
            });
            setClients(response.data);
        } catch (err) {
            console.error('Error fetching clients:', err.response ? err.response.data : err.message);
            setError('Failed to load clients for selection. Please check your backend server and authentication.');
        }
    };

    // Function to fetch all active and historical records from the backend
    const fetchRecords = async () => {
        try {
            const activeResponse = await axios.get(API_URL);
            setActiveRecords(activeResponse.data);

            const historyResponse = await axios.get(HISTORY_API_URL);
            setHistoryRecords(historyResponse.data);
        } catch (error) {
            console.error('Error fetching records:', error);
            setModalMessage('Failed to load records. Please check if the backend server is running.');
            setModalAction(() => () => setModalMessage(''));
        }
    };

    // Function to filter out invalid characters for the FeaturedImage field (unchanged)
    const filterInvalidChars = (value) => {
        const invalidCharsRegex = /[\\/:*?"<>|]/g;
        return value.replace(invalidCharsRegex, '');
    };

    // Handler for changes in the "Add New Record" input fields (unchanged)
    const handleNewRecordChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === 'featuredImage') {
            newValue = filterInvalidChars(value);
        }
        setNewRecordData((prevData) => ({
            ...prevData,
            [name]: newValue,
        }));
    };

    // --- UPDATED HANDLER TO SELECT A CLIENT CREDENTIAL FROM THE DROPDOWN (unchanged) ---
    const handleClientSelect = (credential) => {
        setNewRecordData((prevData) => ({
            ...prevData,
            WP_SITE_URL: credential.siteUrl,
            WP_USERNAME: credential.username,
            WP_APP_PASSWORD: credential.appPassword,
        }));
        setIsClientsDropdownOpen(false);
    };

    // Function to handle auto-filling content fields using the Gemini API (unchanged)
    const handleGenerateContent = async () => {
        setIsLoading(true);
        setApiError(null);

        const prompt = `
            You are a content idea generator. Provide a single, concise JSON object with creative ideas for a content record.
            The content should be professional and suitable for a marketing blog.

            The JSON object should have the following properties:
            - topic (string): a compelling and specific blog post title.
            - audience (string): a clear description of the target audience.
            - keywords (string): a comma-separated list of 3-5 relevant keywords.
            - linkword (string): a short, catchy phrase for an internal link.
            - linkurl (string): a placeholder URL for the link, e.g., 'https://yourwebsite.com/new-blog-post'.
            - featuredImage (string): a short, descriptive phrase that could be used to generate a featured image.

            Example: {
                "topic": "The Future of AI in Content Marketing",
                "audience": "Marketing Professionals and Small Business Owners",
                "keywords": "AI marketing, content strategy, future of content",
                "linkword": "Discover AI tools",
                "linkurl": "https://example.com/ai-tools-for-content",
                "featuredImage": "A stylized robot writing a blog post on a computer"
            }
        `;

        const payload = {
            contents: [{
                role: "user",
                parts: [{ text: prompt }]
            }],
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: "OBJECT",
                    properties: {
                        "topic": { "type": "STRING" },
                        "audience": { "type": "STRING" },
                        "keywords": { "type": "STRING" },
                        "linkword": { "type": "STRING" },
                        "linkurl": { "type": "STRING" },
                        "featuredImage": { "type": "STRING" },
                        "publishDate": { "type": "STRING" },
                        "publishTime": { "type": "STRING" }
                    },
                    "propertyOrdering": ["topic", "audience", "keywords", "linkword", "linkurl", "featuredImage","publishDate", "publishTime"]
                }
            }
        };

        const apiKey = process.env.REACT_APP_GENERATIVE_LANGUAGE_CLIENT;
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent?key=${apiKey}`;

        let attempts = 0;
        const maxAttempts = 5;
        const initialDelay = 1000;

        const fetchWithRetry = async () => {
            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (response.status === 429) {
                    if (attempts < maxAttempts) {
                        const delay = initialDelay * Math.pow(2, attempts);
                        attempts++;
                        await new Promise(res => setTimeout(res, delay));
                        return fetchWithRetry();
                    } else {
                        throw new Error("API request failed after multiple retries due to rate limiting.");
                    }
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(`API error: ${errorData.error.message}`);
                }

                const result = await response.json();
                const jsonText = result?.candidates?.[0]?.content?.parts?.[0]?.text;

                if (!jsonText) {
                    throw new Error("Invalid JSON response format from the API.");
                }

                const parsedJson = JSON.parse(jsonText);

                setNewRecordData(prevData => ({
                    ...prevData,
                    topic: parsedJson.topic,
                    audience: parsedJson.audience,
                    keywords: parsedJson.keywords,
                    linkword: parsedJson.linkword,
                    linkurl: parsedJson.linkurl,
                    featuredImage: parsedJson.featuredImage,
                    publishDate: parsedJson.publishDate,
                    publishTime: parsedJson.publishTime,
                }));

            } catch (error) {
                console.error('Error generating content:', error);
                setApiError(`Failed to generate content: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchWithRetry();
    };

    // Handler to add a new CRM record to the database
    const handleAddNewRecord = async () => {
        if (!newRecordData.topic.trim()) {
            setModalMessage('Topic is required for a new record!');
            setModalAction(() => () => setModalMessage(''));
            return;
        }

        const isDuplicate = activeRecords.some(
            (record) => record.topic.toLowerCase() === newRecordData.topic.toLowerCase()
        );

        if (isDuplicate) {
            setModalMessage('You already have a record with this topic. Please choose a different one.');
            setModalAction(() => () => setModalMessage(''));
            return;
        }

        try {
            const response = await axios.post(API_URL, newRecordData);
            setActiveRecords([...activeRecords, response.data]);
            
            // Clear new record data and sessionStorage after successful addition
            setNewRecordData({
                topic: '',
                audience: '',
                keywords: '',
                WP_SITE_URL: '',
                WP_USERNAME: '',
                WP_APP_PASSWORD: '',
                linkword: '',
                linkurl: '',
                featuredImage: '',
                shortblog: 'No',
                publishDate: '',
                publishTime: '',
            });
            sessionStorage.removeItem('newRecordData');
        } catch (error) {
            console.error('Error adding new record:', error);
            setModalMessage('Failed to add new record. Please try again.');
            setModalAction(() => () => setModalMessage(''));
        }
    };

    // Handler to set a row into editing mode
    const handleEditClick = (record) => {
        setEditingRowId(record._id);
        setEditedData({ ...record });
        // sessionStorage is updated automatically by the useEffect hooks
    };

    // Handler for changes in the input fields of an editing row
    const handleEditedDataChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === 'featuredImage') {
            newValue = filterInvalidChars(value);
        }
        setEditedData((prevData) => ({
            ...prevData,
            [name]: newValue,
        }));
    };

    // Handler to save changes to an existing CRM record
    const handleSaveClick = async (id) => {
        if (!editedData.topic.trim()) {
            setModalMessage('Topic cannot be empty!');
            setModalAction(() => () => setModalMessage(''));
            return;
        }
        try {
            const response = await axios.put(`${API_URL}/${id}`, editedData);
            setActiveRecords(activeRecords.map((rec) => (rec._id === id ? response.data : rec)));
            
            // Clear editing state and sessionStorage after successful save
            setEditingRowId(null);
            setEditedData({});
            sessionStorage.removeItem('editingRowId');
            sessionStorage.removeItem('editedData');
        } catch (error) {
            console.error('Error updating record:', error);
            setModalMessage('Failed to update record. Please try again.');
            setModalAction(() => () => setModalMessage(''));
        }
    };

    // Handler to cancel editing a CRM record
    const handleCancelEdit = () => {
        setEditingRowId(null);
        setEditedData({});
        
        // Clear sessionStorage on cancel
        sessionStorage.removeItem('editingRowId');
        sessionStorage.removeItem('editedData');
    };

    // NEW: Handler to delete a record from the main table and add it to the history table (unchanged)
    const handleDeleteClick = (id) => {
        setModalMessage('Are you sure you want to delete this record? It will be moved to the history summary.');
        setModalAction(() => async () => {
            try {
                const recordToDelete = activeRecords.find(rec => rec._id === id);

                const historyRecordData = {
                    recordId: recordToDelete._id,
                    topic: recordToDelete.topic,
                    audience: recordToDelete.audience,
                    keywords: recordToDelete.keywords,
                    linkword: recordToDelete.linkword,
                    linkurl: recordToDelete.linkurl,
                    featuredImage: recordToDelete.featuredImage, 
                    publishDate:recordToDelete.publishDate,
                    publishTime:recordToDelete.publishTime,
                    shortblog: recordToDelete.shortblog,
                    WP_SITE_URL: recordToDelete.WP_SITE_URL,
                    WP_USERNAME: recordToDelete.WP_USERNAME,
                    WP_APP_PASSWORD: recordToDelete.WP_APP_PASSWORD,
                    createdAt: recordToDelete.createdAt,
                    deletedAt: new Date().toISOString(),
                };
                const historyResponse = await axios.post(HISTORY_API_URL, historyRecordData);

                await axios.delete(`${API_URL}/${id}`);

                setActiveRecords(activeRecords.filter(rec => rec._id !== id));
                setHistoryRecords([...historyRecords, historyResponse.data]);

                setModalMessage('');
            } catch (error) {
                console.error('Error deleting record and creating history:', error);
                setModalMessage('Failed to delete record. Please check your backend connection.');
            }
        });
    };

    // NEW: Handler to add a record from the history summary back to the main table (unchanged)
    const handleAddAgainClick = async (recordToAdd) => {
        const newRecord = {
            topic: recordToAdd.topic,
            audience: recordToAdd.audience,
            keywords: recordToAdd.keywords,
            WP_SITE_URL: recordToAdd.WP_SITE_URL,
            WP_USERNAME: recordToAdd.WP_USERNAME,
            WP_APP_PASSWORD: recordToAdd.WP_APP_PASSWORD,
            linkword: recordToAdd.linkword,
            linkurl: recordToAdd.linkurl,
            featuredImage: recordToAdd.featuredImage,
            shortblog: recordToAdd.shortblog,
            publishDate:recordToAdd.publishDate,
            publishTime:recordToAdd.publishTime,
        };

        try {
            const newActiveRecordResponse = await axios.post(API_URL, newRecord);
            await axios.delete(`${HISTORY_API_URL}/${recordToAdd._id}`);
            setActiveRecords([...activeRecords, newActiveRecordResponse.data]);
            setHistoryRecords(historyRecords.filter(rec => rec._id !== recordToAdd._id));
        } catch (error) {
            console.error('Error adding record again:', error);
            setModalMessage('Failed to add record again. Please check your backend connection.');
            setModalAction(() => () => setModalMessage(''));
        }
    };

    // New function to update records with a downloaded timestamp (unchanged)
    const markRecordsAsDownloaded = async () => {
        const downloadDate = new Date().toISOString();
        try {
            const updatePromises = activeRecords.map(record => {
                const updateData = { lastDownloaded: downloadDate };
                return axios.put(`${API_URL}/${record._id}`, updateData);
            });
            await Promise.all(updatePromises);
            setActiveRecords(activeRecords.map(record => ({ ...record, lastDownloaded: downloadDate })));
        } catch (error) {
            console.error('Error marking records as downloaded:', error);
            setApiError('Failed to save downloaded status to the database.');
        }
    };

    // Function to generate and download the CRM data as an Excel file (unchanged)
    const generateCrmExcel = () => {
        if (activeRecords.length === 0) {
            setModalMessage('No CRM data to export. Please add some data first.');
            setModalAction(() => () => setModalMessage(''));
            return;
        }

        const headers = [
            'topic',
            'audience',
            'keywords',
            'WP_SITE_URL',
            'WP_USERNAME',
            'WP_APP_PASSWORD',
            'linkword',
            'linkurl',
            'featuredImage',
            'shortblog',
            'publishDate', 
            'publishTime',
            'created at',
            'last downloaded'
        ];

        const dataToExport = activeRecords.map((row) => [
            row.topic,
            row.audience,
            row.keywords,
            row.WP_SITE_URL,
            row.WP_USERNAME,
            row.WP_APP_PASSWORD,
            row.linkword,
            row.linkurl,
            row.featuredImage,
            row.shortblog,
            row.publishDate,
            row.publishTime,
            new Date(row.createdAt).toLocaleDateString(),
            row.lastDownloaded ? new Date(row.lastDownloaded).toLocaleDateString() : 'Never',
        ]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...dataToExport]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Content CRM Data');

        const date = new Date('2025-08-09T00:00:00Z');
        const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        const fileName = `Content_CRM_Data_${dateString}.xlsx`;

        XLSX.writeFile(wb, fileName);

        markRecordsAsDownloaded();
    };

 
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 flex flex-col items-center py-10 px-4 font-inter">
    

      <main className="w-full max-w-7xl">
        {apiError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{apiError}</span>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: All Content Records and Inputs */}
          <div className="flex-1 min-w-0">
            {/* Clients Dropdown Button and Menu */}
            <section className="mb-8">
              <div className="flex justify-center relative">
                <button
                  ref={buttonRef}
                  onClick={() => setIsClientsDropdownOpen(!isClientsDropdownOpen)}
                  className="px-6 py-2 bg-blue-500 text-white font-medium rounded-md shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 flex items-center transition duration-150 ease-in-out"
                >
                  Select Client
                  <svg className={`w-4 h-4 ml-2 transition-transform duration-200 ${isClientsDropdownOpen ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {isClientsDropdownOpen && (
                  <div ref={dropdownRef} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    {clients.length > 0 ? (
                      clients.map((client) => (
                        client.websiteCredentials && client.websiteCredentials.map((credential, index) => (
                          <div
                            key={`${client._id}-${index}`}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                            onClick={() => handleClientSelect(credential)}
                          >
                            <span className="font-semibold">{client.clientName}</span> - {credential.siteUrl}
                          </div>
                        ))
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">No clients found.</div>
                    )}
                  </div>
                )}
              </div>
            </section>
            {/* CRM Records Table Section */}
            <section className="bg-white rounded-2xl shadow-xl p-6 mb-10 border border-gray-200">
              <h2 className="text-2xl font-bold mb-6 text-blue-700 text-center">All Content Records</h2>
              {error && <div className="text-red-500 text-center mb-4">{error}</div>}
              {/* Portrait Form for New Record */}
              <div className="bg-blue-50 rounded-lg p-4 mb-8 shadow flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-blue-700 mb-2">Add New Content Record</h3>
                <input type="text" name="topic" value={newRecordData.topic} onChange={handleNewRecordChange} placeholder="Topic" className="excel-input" />
                <input type="text" name="audience" value={newRecordData.audience} onChange={handleNewRecordChange} placeholder="Audience" className="excel-input" />
                <input type="text" name="keywords" value={newRecordData.keywords} onChange={handleNewRecordChange} placeholder="Keywords" className="excel-input" />
                <input type="url" name="WP_SITE_URL" value={newRecordData.WP_SITE_URL} onChange={handleNewRecordChange} placeholder="WP Site URL" className="excel-input" />
                <input type="text" name="WP_USERNAME" value={newRecordData.WP_USERNAME} onChange={handleNewRecordChange} placeholder="WP Username" className="excel-input" />
                <input type="password" name="WP_APP_PASSWORD" value={newRecordData.WP_APP_PASSWORD} onChange={handleNewRecordChange} placeholder="WP App Password" className="excel-input" />
                <input type="text" name="linkword" value={newRecordData.linkword} onChange={handleNewRecordChange} placeholder="Link Word" className="excel-input" />
                <input type="url" name="linkurl" value={newRecordData.linkurl} onChange={handleNewRecordChange} placeholder="Link URL" className="excel-input" />
                <input type="text" name="featuredImage" value={newRecordData.featuredImage} onChange={handleNewRecordChange} placeholder="Featured Image" className="excel-input" />
                <input type="text" name="publishDate" value={newRecordData.publishDate} onChange={handleNewRecordChange} placeholder="Publish Date" className="excel-input" />
                <input type="text" name="publishTime" value={newRecordData.publishTime} onChange={handleNewRecordChange} placeholder="Publish Time" className="excel-input" />
                <select name="shortblog" value={newRecordData.shortblog} onChange={handleNewRecordChange} className="excel-input">
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                <div className="flex gap-2">
                  <button onClick={handleAddNewRecord} className="action-button save-button w-full">
                    Add New
                  </button>
                  <button
                    onClick={handleGenerateContent}
                    disabled={isLoading}
                    className={`action-button ${isLoading ? 'bg-gray-400' : 'bg-green-500'} text-white w-full`}
                  >
                    {isLoading ? 'Generating...' : 'Auto-Fill Content'}
                  </button>
                </div>
              </div>
              {/* Portrait Cards for Each Record */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeRecords.length > 0 ? (
                  activeRecords.map((record) => (
                    <div key={record._id} className="bg-white border border-gray-200 rounded-xl shadow p-6 flex flex-col gap-2 relative">
                      {editingRowId === record._id ? (
                        <>
                          <input type="text" name="topic" value={editedData.topic || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Topic" />
                          <input type="text" name="audience" value={editedData.audience || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Audience" />
                          <input type="text" name="keywords" value={editedData.keywords || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Keywords" />
                          <input type="url" name="WP_SITE_URL" value={editedData.WP_SITE_URL || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="WP Site URL" />
                          <input type="text" name="WP_USERNAME" value={editedData.WP_USERNAME || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="WP Username" />
                          <input type="text" name="WP_APP_PASSWORD" value={editedData.WP_APP_PASSWORD || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="WP App Password" />
                          <input type="text" name="linkword" value={editedData.linkword || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Link Word" />
                          <input type="url" name="linkurl" value={editedData.linkurl || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Link URL" />
                          <input type="text" name="featuredImage" value={editedData.featuredImage || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Featured Image" />
                          <input type="text" name="publishDate" value={editedData.publishDate || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Publish Date" />
                          <input type="text" name="publishTime" value={editedData.publishTime || ''} onChange={handleEditedDataChange} className="excel-input" placeholder="Publish Time" />
                          <select name="shortblog" value={editedData.shortblog || 'No'} onChange={handleEditedDataChange} className="excel-input">
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => handleSaveClick(record._id)} className="action-button save-button w-full">Save</button>
                            <button onClick={handleCancelEdit} className="action-button cancel-button w-full">Cancel</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div><span className="font-semibold">Topic:</span> {record.topic || '-'}</div>
                          <div><span className="font-semibold">Audience:</span> {record.audience || '-'}</div>
                          <div><span className="font-semibold">Keywords:</span> {record.keywords || '-'}</div>
                          <div><span className="font-semibold">WP_SITE_URL:</span> {record.WP_SITE_URL || '-'}</div>
                          <div><span className="font-semibold">WP_USERNAME:</span> {record.WP_USERNAME || '-'}</div>
                          <div><span className="font-semibold">WP_APP_PASSWORD:</span> {record.WP_APP_PASSWORD || '-'}</div>
                          <div><span className="font-semibold">Link Word:</span> {record.linkword || '-'}</div>
                          <div><span className="font-semibold">Link URL:</span> <a href={record.linkurl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{record.linkurl || '-'}</a></div>
                          <div><span className="font-semibold">Featured Image:</span> {record.featuredImage || '-'}</div>
                          <div><span className="font-semibold">Publish Date:</span> {record.publishDate || '-'}</div>
                          <div><span className="font-semibold">Publish Time:</span> {record.publishTime || '-'}</div>
                          <div><span className="font-semibold">Short Blog:</span> {record.shortblog || '-'}</div>
                          <div><span className="font-semibold">Created At:</span> {new Date(record.createdAt).toLocaleDateString()}</div>
                          <div><span className="font-semibold">Last Downloaded:</span> <span className={`${record.lastDownloaded ? 'text-green-600 font-bold' : 'text-gray-500 italic'}`}>{record.lastDownloaded ? new Date(record.lastDownloaded).toLocaleDateString() : 'Never'}</span></div>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => handleEditClick(record)} className="action-button edit-button w-full">Edit</button>
                            <button onClick={() => handleDeleteClick(record._id)} className="action-button delete-button w-full">Delete</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="col-span-1 md:col-span-2 text-center text-gray-500 py-8">No active records found. Add a new record above!</div>
                )}
              </div>

        {/* Export CRM Data Button */}
        <div className="flex justify-center mt-6 mb-12">
          <button
            onClick={generateCrmExcel}
            className="px-8 py-4 bg-purple-600 text-white font-extrabold text-lg rounded-md shadow-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-150 ease-in-out transform hover:scale-105"
          >
            Download CRM Data as Excel
          </button>
        </div>

        </section>

          </div>
          {/* Right: History */}
          <div className="w-full lg:w-1/3 flex-shrink-0">
            {historyRecords.length > 0 && (
              <section className="bg-gray-50 p-6 rounded-2xl shadow-inner border border-red-200 h-full">
                <h3 className="text-xl font-semibold mb-4 text-gray-800 text-center">Deleted Content Records History</h3>
                <ul className="list-disc pl-5 space-y-2">
                  {historyRecords.map(record => (
                    <li key={record._id} className="text-gray-500 flex justify-between items-center italic">
                      <div>
                        <span className="font-medium line-through">{record.topic}</span> - Deleted on: <span className="font-semibold">{new Date(record.deletedAt).toLocaleDateString()}</span>
                      </div>
                      <button
                        onClick={() => handleAddAgainClick(record)}
                        className="px-4 py-2 text-sm font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Add Again
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </main>
      <Modal 
        message={modalMessage} 
        onConfirm={modalAction} 
        onCancel={() => { setModalMessage(''); setModalAction(null); }}
      />
    </div>
  );
};

export default ExcelGen;
